import React from 'react';
import {
  View,
  Text,
  Pressable,
  Linking,
  ImageBackground,
  StyleSheet,
} from 'react-native';
import PropTypes from 'prop-types';
import Image from '@web-components/common/Image';
import { footerButtons } from '~global/constants';
import {
  footer,
  footerTopLogo,
  footerTopBg,
  footerTopText,
  footerBarcode,
  footerLogoSmall,
} from '~global/footer';
import { isWeb, isNative, maxWidthContainer } from '~global/screens';
import flex from '@web/styles/flex';
import Colors from '~global/Colors';

const { flexRow, alignSelfStretch, alignItemsCenter } = flex;
const currentYear = new Date().getFullYear();

const FooterWeb = ({ navigation }) => {
  const appsDownload = (url) => {
    if (url && !isNative) {
      window.dataLayer.push({
        event: 'Download Lilla',
        downloadLink: url,
      });
      Linking.openURL(url);
    }
  };

  return (
    <>
      <TopFooter />

      <View style={styles.mainFooterContainer}>
        <View style={styles.footerWrapper}>
          <View style={styles.footerMainColumn1}>
            <Text style={styles.mainFooterTitle}>Information</Text>
            {footer.links.web.map((item, index) => (
              <Text
                onPress={() => navigation.navigate(item.screen)}
                style={styles.mainFooterText}
                key={`information${index}`}>
                {item.name}
              </Text>
            ))}
          </View>
          <View style={styles.footerMainColumn2}>
            <Text style={styles.mainFooterTitle}>Payment Options</Text>
            {footer.payments.map((row, index) => (
              <View key={`row-${index}`} style={flexRow}>
                {row.map((item, index) => (
                  <Image
                    key={`image-${index}`}
                    style={styles.mainFooterOption}
                    source={{ uri: item.uri }}
                    resizeMode={'contain'}
                  />
                ))}
              </View>
            ))}
          </View>
          <View style={styles.footerMainColumn3}>
            <Text style={styles.mainFooterTitle}>Customer Care</Text>
            <View style={styles.containerCustomerCareConsumers}>
              <Text style={styles.footerTextMedium}>
                {footer.customerCareConsumers.title}
              </Text>
              <Text style={styles.footerTextMedium}>
                {footer.customerCareConsumers.subTitle}
              </Text>
            </View>
            <View>
              {footer.services.map((item) => (
                <Pressable
                  key={item.key}
                  style={styles.cursorPointer}
                  onPress={
                    item.key === 'help-center'
                      ? () => navigation.navigate(item.screen)
                      : item.useLinking(item.link)
                  }>
                  <Text style={styles.mainFooterSubTitle}>{item.name[0]}</Text>
                  {item.key !== 'help-center' && (
                    <Text style={styles.mainFooterSubText}>{item.name[1]}</Text>
                  )}
                </Pressable>
              ))}
            </View>
            <View style={styles.divider} />
            <View>
              <Text style={styles.footerTextMedium}>
                {footer.safetyConsumers.title}
              </Text>
              <Pressable
                onPress={footer.safetyConsumers.useLinking(
                  footer.safetyConsumers.link,
                )}>
                <Text style={styles.footerTextBold}>
                  {footer.safetyConsumers.contact}{' '}
                  {footer.safetyConsumers.subTitle}
                </Text>
              </Pressable>
            </View>
          </View>
          <View style={[styles.footerMainColumn4, flexRow]}>
            <Image
              style={styles.barcodeDownloadImage}
              source={{ uri: footerBarcode }}
            />
            <View>
              <View style={styles.footerLogoContainer}>
                <Image
                  source={{ uri: footerLogoSmall }}
                  style={styles.footerLogoSmall}
                />
                <Text style={styles.footerLogoText}>
                  The First Ultimate App{'\n'}for Moms
                </Text>
              </View>
              <View style={flexRow}>
                {footerButtons.map((item, index) => (
                  <DownloadButton
                    key={item.key}
                    imageSource={item.src}
                    imageStyle={styles.imageStyle}
                    onPress={() => appsDownload(item.url)}
                  />
                ))}
              </View>
            </View>
          </View>
        </View>
      </View>

      <BottomFooter />
    </>
  );
};

const TopFooter = (props) => {
  return (
    <View style={styles.topFooterContainer}>
      <ImageBackground
        style={styles.footerBackground}
        source={{ uri: footerTopBg }}>
        <View style={styles.footerTopColumn1}>
          <Image style={styles.footerTopLogo} source={{ uri: footerTopLogo }} />
        </View>
        <View style={styles.footerTopColumn2}>
          <Image style={styles.footerTopText} source={{ uri: footerTopText }} />
        </View>
      </ImageBackground>
    </View>
  );
};

const DownloadButton = (props) => {
  const { onPress, imageStyle, imageSource } = props;
  return (
    <Pressable style={styles.cursorPointer} onPress={onPress}>
      <Image
        style={imageStyle}
        source={{
          uri: imageSource,
        }}
      />
    </Pressable>
  );
};

DownloadButton.propTypes = {
  onPress: PropTypes.func,
  imageStyle: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.number,
  ]),
  imageSource: PropTypes.string,
};

const BottomFooter = (props) => {
  return (
    <View style={styles.bottomFooterContainer}>
      <View
        style={[
          styles.footerWrapper,
          flexRow,
          alignSelfStretch,
          alignItemsCenter,
        ]}>
        <View style={styles.bottomFooterColumn1}>
          <Text style={styles.bottomFooterText}>
            {currentYear} © PT. Sociolla Ritel Indonesia. All Rights Reserved
          </Text>
        </View>
        <View style={styles.bottomFooterColumn2}>
          <View style={styles.connectContainer}>
            <View style={styles.marginRight30}>
              <Text style={styles.connectText}>Connect with us</Text>
            </View>
            <View style={flexRow}>
              <View style={styles.connectIconWrapper}>
                {footer.socialsWeb.map(({ key, uri }) => (
                  <Pressable key={key} style={styles.bottomFooterIcon}>
                    <Image
                      style={styles.bottomFooterIconSize}
                      source={{ uri }}
                    />
                  </Pressable>
                ))}
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cursorPointer: {
    ...(isWeb && {
      cursor: 'pointer',
      paddingVertical: 4,
    }),
  },
  footerBackground: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    height: 120,
  },
  footerTopColumn1: {
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: Colors.white,
    borderBottomRightRadius: 40,
    justifyContent: 'center',
    flex: 2,
  },
  footerTopColumn2: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 8,
  },
  footerTopText: { width: 763, height: 56 },
  topFooterContainer: {
    backgroundColor: Colors.purple26,
    ...(isWeb && { boxShadow: '0 0 1px 0 #6F6F6F' }),
    height: 120,
  },
  mainFooterContainer: {
    backgroundColor: Colors.pastel9,
    justifyContent: 'center',
    flexDirection: 'row',
    paddingTop: 40,
    paddingBottom: 30,
  },
  mainFooterTitle: {
    color: Colors.purple7,
    fontFamily: 'satoshi-black',
    fontSize: 20,
    lineHeight: 25,
    marginBottom: 15,
  },
  mainFooterSubTitle: {
    color: Colors.purple7,
    fontFamily: 'satoshi-bold',
    fontSize: 16,
    lineHeight: 29,
  },
  mainFooterText: {
    color: Colors.grey2,
    fontFamily: 'satoshi-medium',
    fontSize: 16,
    lineHeight: 32,
  },
  mainFooterSubText: {
    color: Colors.purple7,
    fontFamily: 'satoshi-medium',
    fontSize: 16,
    lineHeight: 29,
  },
  mainFooterOption: {
    marginRight: 7,
    marginBottom: 7,
    height: 30,
    width: 50,
  },
  bottomFooterContainer: {
    alignItems: 'center',
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'center',
    height: 40,
  },
  bottomFooterColumn1: { flex: 5 },
  bottomFooterColumn2: { alignSelf: 'stretch', flex: 3 },
  bottomFooterText: {
    color: Colors.purple7,
    fontFamily: 'satoshi-medium',
    fontSize: 13,
    lineHeight: 16,
  },
  bottomFooterIcon: {
    ...(isWeb && { cursor: 'pointer' }),
    marginHorizontal: 10,
  },
  bottomFooterIconSize: {
    width: 21,
    height: 21,
  },
  footerWrapper: {
    flexDirection: 'row',
    width: maxWidthContainer,
  },
  connectContainer: {
    backgroundColor: Colors.purple35,
    borderTopLeftRadius: 25,
    borderTopRightRadius: 25,
    flexDirection: 'row',
    paddingVertical: 25,
    paddingHorizontal: 30,
    position: 'absolute',
    bottom: 0,
  },
  connectText: {
    fontFamily: 'satoshi-bold',
    fontSize: 16,
    lineHeight: 20,
    marginRight: 10,
  },
  connectIconWrapper: {
    flexDirection: 'row',
    marginHorizontal: -10,
  },
  imageStyle: {
    width: 138,
    height: 45,
    marginRight: 10,
  },
  footerMainColumn1: { flex: 1.5 },
  footerMainColumn2: { flex: 2 },
  footerMainColumn3: { flex: 1.5 },
  footerMainColumn4: { flex: 3 },
  barcodeDownloadImage: {
    height: 120,
    width: 123,
    marginRight: 20,
  },
  footerTopLogo: { width: 159.72, height: 36.46 },
  footerLogoContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 20,
  },
  footerLogoSmall: {
    width: 40,
    height: 40,
    marginRight: 15,
  },
  footerLogoText: {
    color: Colors.purple7,
    fontFamily: 'satoshi-bold',
    fontSize: 20,
    lineHeight: 25,
  },
  divider: {
    backgroundColor: '#E4E3E6',
    height: 1,
    marginVertical: 15,
  },
  footerTextBold: {
    color: Colors.grey17,
    fontFamily: 'satoshi-bold',
    fontSize: 11,
    lineHeight: 18,
  },
  footerTextMedium: {
    color: Colors.grey17,
    fontFamily: 'satoshi-medium',
    fontSize: 11,
    lineHeight: 18,
  },
  containerCustomerCareConsumers: {
    marginBottom: 4,
  },
});

export default FooterWeb;
